import React, { useContext, useEffect, useState, useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import useDebouncedSearchWithHistory from '../../../../../hooks/useDebouncedSearchWithHistory';
import { UiContext } from '../../../../../context/UiContext';
import tableColsToSortOptions from '../../../../../helpers/tableColsToSortOptions';
import AdminService from '../../../../../services/AdminService';
import TableMenu from '../../TableMenu';
import Table from '../../../../Table';
import ViewInterviewer from './ViewInterviewer';
import InterviewerSidePanel from './InterviewerSidePanel';
import classes from './styles.module.scss';

export default function ExternalInterviewers({ setItemCount }) {
  const [activeAction, setActiveAction] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedRows, setSelectedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isViewInterviewerVisible, setIsViewInterviewerVisible] =
    useState(false);
  const [currentInterviewerId, setCurrentInterviewerId] = useState(null);
  const [isEditInterviewerVisible, setIsEditInterviewerVisible] =
    useState(false);
  const { t } = useTranslation();
  const [activeSortOption, setActiveSortOption] = useState({
    label: t('common.createdOn'),
    value: 'createdAt',
  });

  const { debouncedSearchTerm, searchHistory } = useDebouncedSearchWithHistory(
    searchTerm,
    'managersSearchHistory'
  );

  const {
    showModal,
    showConflictModal,
    showUnknownErrorModal,
    showSelectionNeededModal,
    showMinSelectionNeededModal,
    setIsFetching,
  } = useContext(UiContext);

  const {
    data: externalInterviewersPages,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: [
      'externalInterviewers',
      sortOrder,
      activeSortOption.value,
      debouncedSearchTerm,
      currentPage,
    ],
    queryFn: () =>
      AdminService.getExternalInterviewers({
        orderBy: activeSortOption.value || 'id',
        term: debouncedSearchTerm,
        sortOrder,
        pageNumber: currentPage,
      }),
    keepPreviousData: true,
  });

  useEffect(() => {
    if (externalInterviewersPages) {
      setItemCount(externalInterviewersPages.totalCount);
    }
  }, [externalInterviewersPages, setItemCount]);

  useEffect(() => {
    refetch();
  }, [activeSortOption, debouncedSearchTerm, refetch, sortOrder]);

  const baseRows = externalInterviewersPages?.data || [];
  const rows = useMemo(
    () =>
      baseRows.map((m) => ({
        ...m,
        accessGranted: !!m.userId,
      })),
    [baseRows]
  );

  const columns = [
    {
      name: t(
        'dashboardComponents.Console.ExternalInterviewers.columns.firstName'
      ),
      field: 'firstName',
      sortField: 'firstName',
      sortable: true,
    },
    {
      name: t(
        'dashboardComponents.Console.ExternalInterviewers.columns.lastName'
      ),
      field: 'lastName',
      sortField: 'lastName',
      sortable: true,
    },
    {
      name: t(
        'dashboardComponents.Console.ExternalInterviewers.columns.designation'
      ),
      field: 'designation',
      sortable: true,
    },
    {
      name: t('dashboardComponents.Console.ExternalInterviewers.columns.email'),
      field: 'email',
      sortable: true,
    },
    {
      name: t('dashboardComponents.Console.ExternalInterviewers.columns.phone'),
      field: 'phone',
      sortable: true,
    },
    {
      name: t(
        'dashboardComponents.Console.ExternalInterviewers.columns.accessGranted'
      ),
      field: 'accessGranted',
      type: 'boolean',
      sortable: false,
    },
  ];

  const grantAccess = async () => {
    try {
      if (!selectedRows.length) {
        showSelectionNeededModal();
        return;
      }

      if (selectedRows.length > 1) {
        showMinSelectionNeededModal();
        return;
      }

      setIsFetching(true);
      const promises = selectedRows.map((id) =>
        AdminService.grantExternalInterviewerAccess({ id })
      );
      await Promise.all(promises);
      showModal({
        title: t(
          'dashboardComponents.Console.ExternalInterviewers.grantAccess.successTitle'
        ),
        text: t(
          'dashboardComponents.Console.ExternalInterviewers.grantAccess.grantText'
        ),
        dismissButtonLabel: t('common.gotIt'),
        dismissButtonVariant: '',
      });
    } catch (error) {
      if (error.response.status === 409) {
        showConflictModal(
          t(
            'dashboardComponents.Console.ExternalInterviewers.grantAccess.conflictText'
          )
        );
      } else {
        showUnknownErrorModal();
      }
    } finally {
      await refetch();
      setIsFetching(false);
    }
  };

  const removeAccess = async () => {
    try {
      if (!selectedRows.length) {
        showSelectionNeededModal();
        return;
      }
      if (selectedRows.length > 1) {
        showMinSelectionNeededModal();
        return;
      }
      setIsFetching(true);
      const promises = selectedRows.map((id) =>
        AdminService.removeExternalInterviewerAccess({ id })
      );
      await Promise.all(promises);
      showModal({
        title: t(
          'dashboardComponents.Console.ExternalInterviewers.removeAccess.successTitle'
        ),
        text: t(
          'dashboardComponents.Console.ExternalInterviewers.removeAccess.removeText'
        ),
        dismissButtonLabel: t('common.gotIt'),
        dismissButtonVariant: '',
      });
    } catch (error) {
      showUnknownErrorModal();
    } finally {
      await refetch();
      setIsFetching(false);
    }
  };

  const deleteData = () => {
    showModal({
      icon: 'delete',
      title: t(
        'dashboardComponents.Console.ExternalInterviewers.modals.deleteInterviewer.title'
      ),
      text: t(
        'dashboardComponents.Console.ExternalInterviewers.modals.deleteInterviewer.confirmationText'
      ),
      onConfirm: async () => {
        try {
          setIsFetching(true);
          const promises = selectedRows.map((row) =>
            AdminService.deleteExternalInterviewer({ id: row })
          );
          await Promise.all(promises);
          await refetch();
          setCurrentInterviewerId(null);
          setSelectedRows([]);
        } catch (error) {
          console.log(error);

          if (error.response.status === 409) {
            showConflictModal(error.response.data.message);
          } else {
            showUnknownErrorModal();
          }
        } finally {
          setIsFetching(false);
        }
      },
      onCancel: () => {},
      dismissButtonLabel: t('common.no'),
      confirmButtonLabel: t('common.yes'),
    });
  };

  const showAddInterviewer = () => {
    setCurrentInterviewerId(null);
    setSelectedRows([]);
    setIsViewInterviewerVisible(false);
    setIsEditInterviewerVisible(true);
  };

  const actions = [
    {
      label: t('common.add'),
      value: 'Add',
      onClick: () => {
        showAddInterviewer();
      },
    },
    {
      label: t('common.edit'),
      value: 'Edit',
      onClick: () => {
        if (selectedRows.length) {
          setIsEditInterviewerVisible(true);
        } else {
          showSelectionNeededModal();
        }
      },
    },
    {
      label: t('common.delete'),
      value: 'Delete',
      onClick: () => {
        if (selectedRows.length) {
          deleteData();
        } else {
          showSelectionNeededModal();
        }
      },
    },
    {
      label: t(
        'dashboardComponents.Console.CompanyManagement.managers.actions.grantAccess'
      ),
      value: 'GrantAccess',
      onClick: grantAccess,
    },
    {
      label: t(
        'dashboardComponents.Console.CompanyManagement.managers.actions.removeAccess'
      ),
      value: 'RemoveAccess',
      onClick: removeAccess,
    },
  ];

  const sortOptions = tableColsToSortOptions(columns);

  const showViewInterviewer = (id) => {
    setCurrentInterviewerId(id);
    setIsViewInterviewerVisible(true);
    setTimeout(() => {
      setCurrentInterviewerId(null);
    }, 0);
  };

  return (
    <div className={classes.ExternalInterviewers}>
      <TableMenu
        actions={actions}
        activeAction={activeAction}
        setActiveAction={setActiveAction}
        sortOptions={sortOptions}
        activeSortOption={activeSortOption}
        setActiveSortOption={setActiveSortOption}
        selectedCount={selectedRows.length}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        searchHistory={searchHistory}
        setSortOrder={setSortOrder}
        currentPage={currentPage}
        totalPages={externalInterviewersPages?.totalPages || 1}
        setCurrentPage={setCurrentPage}
        searchResultCount={
          debouncedSearchTerm && !isFetching
            ? externalInterviewersPages?.totalCount
            : null
        }
      />
      <Table
        name="External Interviewers"
        rows={rows}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        sortOptions={sortOptions}
        setActiveSortOption={setActiveSortOption}
        activeSortOption={activeSortOption}
        setSortOrder={setSortOrder}
        sortOrder={sortOrder}
        columns={columns}
        onRowClick={showViewInterviewer}
      />
      <ViewInterviewer
        isVisible={isViewInterviewerVisible}
        handleClose={() => setIsViewInterviewerVisible(false)}
        interviewers={externalInterviewersPages?.data || []}
        currentInterviewerId={currentInterviewerId}
        refetch={refetch}
        setIsEditInterviewerVisible={setIsEditInterviewerVisible}
        setSelectedRows={setSelectedRows}
        isOverlayed={isEditInterviewerVisible}
      />
      <InterviewerSidePanel
        isVisible={isEditInterviewerVisible}
        isEdit={selectedRows.length > 0}
        interviewers={externalInterviewersPages?.data || []}
        selectedRows={selectedRows}
        handleClose={() => setIsEditInterviewerVisible(false)}
        refetch={refetch}
        noAnimation={isViewInterviewerVisible}
      />
    </div>
  );
}
